import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  alpha,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EventAvailable from '@mui/icons-material/EventAvailable'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportClinicBrandCommunicationSummary } from 'src/supportPortal/services/clinicBrandCommunicationDashboardService'
import {
  getSupportClinicBrandCommsProfile,
  saveSupportPatientCommunicationPolicy,
} from 'src/supportPortal/services/clinicBrandCommsConfigService'
import ClinicBrandCommunicationOverview from './ClinicBrandCommunicationOverview'

const REFERRAL_PATIENT_COMMUNICATION_CATEGORIES = ['referral-therapy', 'referral-assessment']
const REFERRAL_OPT_IN_REASON =
  'Clinic brand opted in to patient referral booking follow-ups.'
const REFERRAL_DEFAULT_OFF_REASON =
  'Referral booking follow-ups stay off until the clinic brand opts in.'

function getClinicBrandTitle(brand) {
  return brand?.title || brand?.brandName || brand?.brandShortName || brand?.slug || 'Clinic brand'
}

function normalizePolicyCategory(category) {
  return typeof category === 'string' ? category.trim().toLowerCase() : ''
}

function getReferralPolicyState(config) {
  const policyMap = new Map()
  ;(config?.patientCommunicationCategoryPolicies || []).forEach((policy) => {
    const category = normalizePolicyCategory(policy?.category)
    if (category) {
      policyMap.set(category, policy)
    }
  })

  const enabledCount = REFERRAL_PATIENT_COMMUNICATION_CATEGORIES.filter(
    (category) => policyMap.get(category)?.allowed === true,
  ).length

  return {
    enabled: enabledCount === REFERRAL_PATIENT_COMMUNICATION_CATEGORIES.length,
    partiallyEnabled:
      enabledCount > 0 && enabledCount < REFERRAL_PATIENT_COMMUNICATION_CATEGORIES.length,
  }
}

function buildReferralPolicies(config, enabled) {
  const referralCategories = new Set(REFERRAL_PATIENT_COMMUNICATION_CATEGORIES)
  const existingPolicies = Array.isArray(config?.patientCommunicationCategoryPolicies)
    ? config.patientCommunicationCategoryPolicies
    : []

  const preservedPolicies = existingPolicies.filter(
    (policy) => !referralCategories.has(normalizePolicyCategory(policy?.category)),
  )

  return [
    ...preservedPolicies,
    ...REFERRAL_PATIENT_COMMUNICATION_CATEGORIES.map((category) => ({
      category,
      allowed: enabled,
      metered: true,
      reason: enabled ? REFERRAL_OPT_IN_REASON : REFERRAL_DEFAULT_OFF_REASON,
    })),
  ]
}

/**
 * Lazy communications overview for a single clinic brand. The summary is only fetched when the
 * drawer is opened (button click), instead of eagerly for every brand on the clinics listing.
 */
export default function ClinicBrandCommunicationsDrawer({
  open,
  onClose,
  brand,
  canManagePatientCommunications = false,
}) {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [policyLoading, setPolicyLoading] = useState(false)
  const [policySaving, setPolicySaving] = useState(false)
  const [policyError, setPolicyError] = useState('')
  const [policySuccess, setPolicySuccess] = useState('')
  const [commsConfig, setCommsConfig] = useState(null)
  const brandId = brand?.id
  const referralPolicyState = useMemo(() => getReferralPolicyState(commsConfig), [commsConfig])

  useEffect(() => {
    if (!open || !brandId) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setSummary(null)
      try {
        const resp = await getSupportClinicBrandCommunicationSummary([brandId])
        if (cancelled) return
        if (resp && isCommonResponseSuccessfulV2(resp)) {
          const summaries =
            getDataMapFromCommonResponse(resp)?.clinicBrandCommunicationSummary || []
          setSummary(summaries.find((item) => item?.clinicBrandId === brandId) || summaries[0] || null)
        } else {
          setSummary(null)
        }
      } catch (error) {
        if (!cancelled) setSummary(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, brandId])

  useEffect(() => {
    if (!open || !brandId || !canManagePatientCommunications) {
      setCommsConfig(null)
      setPolicyError('')
      setPolicySuccess('')
      return undefined
    }
    let cancelled = false
    async function loadPolicy() {
      setPolicyLoading(true)
      setPolicyError('')
      setPolicySuccess('')
      setCommsConfig(null)
      try {
        const resp = await getSupportClinicBrandCommsProfile(brandId)
        if (cancelled) return
        if (!resp || !isCommonResponseSuccessfulV2(resp)) {
          setPolicyError(
            getMessageMapFromCommonResponse(resp) || 'Unable to load patient communication settings',
          )
          return
        }
        const data = getDataMapFromCommonResponse(resp) || {}
        const profile = data.clinicBrandCommsProfile || {}
        setCommsConfig(profile.config || data['clinic-brand-comms-config'] || null)
      } catch (error) {
        if (!cancelled) {
          setPolicyError(error?.message || 'Unable to load patient communication settings')
        }
      } finally {
        if (!cancelled) setPolicyLoading(false)
      }
    }
    loadPolicy()
    return () => {
      cancelled = true
    }
  }, [open, brandId, canManagePatientCommunications])

  const handleReferralToggle = async (event) => {
    const enabled = event.target.checked
    if (!brandId) return
    setPolicySaving(true)
    setPolicyError('')
    setPolicySuccess('')
    const nextPolicies = buildReferralPolicies(commsConfig, enabled)
    try {
      const resp = await saveSupportPatientCommunicationPolicy({
        clinicBrandId: brandId,
        patientCommunicationLevel: commsConfig?.patientCommunicationLevel || 'critical-only',
        communicationModesSettings: commsConfig?.communicationModesSettings,
        patientCommunicationCategoryPolicies: nextPolicies,
      })
      if (!resp || !isCommonResponseSuccessfulV2(resp)) {
        throw new Error(
          getMessageMapFromCommonResponse(resp) || 'Unable to save patient communication settings',
        )
      }
      const savedConfig = getDataMapFromCommonResponse(resp)?.['clinic-brand-comms-config'] || {
        ...(commsConfig || {}),
        patientCommunicationCategoryPolicies: nextPolicies,
      }
      setCommsConfig(savedConfig)
      setPolicySuccess(
        enabled
          ? 'Referral booking follow-ups are enabled for therapy and assessment referrals.'
          : 'Referral booking follow-ups are off for therapy and assessment referrals.',
      )
    } catch (error) {
      setPolicyError(error?.message || 'Unable to save patient communication settings')
    } finally {
      setPolicySaving(false)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 820 }, maxWidth: '100%' } }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Communications Overview
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getClinicBrandTitle(brand)}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Stack spacing={2}>
          {canManagePatientCommunications ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                p: { xs: 1.75, sm: 2 },
                borderColor: alpha('#0f172a', 0.1),
                backgroundColor: alpha('#ffffff', 0.96),
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: alpha('#0f9d58', 0.12),
                    color: '#0f9d58',
                    flex: '0 0 auto',
                  }}
                >
                  <EventAvailable fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Patient Communications
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Patient-facing referral drips stay off until this clinic brand opts in.
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={referralPolicyState.enabled ? 'success' : 'default'}
                      variant={referralPolicyState.enabled ? 'filled' : 'outlined'}
                      label={referralPolicyState.enabled ? 'Enabled' : 'Off by default'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>
                        Referral booking follow-ups
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Therapy and assessment recommendations sent after clinician referral.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {policySaving || policyLoading ? <CircularProgress size={18} /> : null}
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            checked={referralPolicyState.enabled}
                            onChange={handleReferralToggle}
                            disabled={policyLoading || policySaving || !commsConfig}
                          />
                        )}
                        label={referralPolicyState.enabled ? 'On' : 'Off'}
                      />
                    </Stack>
                  </Stack>

                  {referralPolicyState.partiallyEnabled ? (
                    <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                      Therapy and assessment referral policies are currently different. Changing this toggle
                      will keep both aligned.
                    </Alert>
                  ) : null}
                  {policyError ? (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                      {policyError}
                    </Alert>
                  ) : null}
                  {policySuccess ? (
                    <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
                      {policySuccess}
                    </Alert>
                  ) : null}
                </Box>
              </Stack>
            </Paper>
          ) : null}
          <ClinicBrandCommunicationOverview summary={summary} loading={loading} />
        </Stack>
      </Box>
    </Drawer>
  )
}
